/*
 * Shared styles for the Base UI wrappers (ADR-W01). State comes from Base UI's
 * data attributes (data-open, data-highlighted, data-checked, data-selected…),
 * so there is no JavaScript styling logic.
 */

/* ---------- Overlays: dialog, alert dialog ---------- */
.backdrop {
  position: fixed;
  inset: 0;
  background: var(--scrim);
  transition: opacity var(--duration-normal) var(--ease-standard);
}

.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  width: min(32rem, calc(100vw - 2 * var(--space-5)));
  max-height: calc(100dvh - 2 * var(--space-6));
  overflow: auto;
  padding: var(--space-6);
  background: var(--surface);
  color: var(--text);
  border: var(--border-width) solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-raised);
  transition:
    opacity var(--duration-normal) var(--ease-standard),
    scale var(--duration-normal) var(--ease-standard);
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  scale: 0.97;
}

.header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.title {
  flex: 1;
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  font-weight: var(--weight-extrabold);
}

.description {
  margin-top: var(--space-2);
  color: var(--text-2);
  font-size: var(--text-md);
}

.closeButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--tap-min);
  height: var(--tap-min);
  margin: calc(-1 * var(--space-2)) calc(-1 * var(--space-2)) 0 0;
  padding: 0;
  color: var(--text-2);
  background: transparent;
  border: 0;
  border-radius: var(--radius-pill);
}

.closeButton:hover {
  background: var(--surface-2);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

/* NoticeDialog: centred, and its one action spans the width (mobile WarningNotice). */
.notice {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.notice .title {
  flex: none;
}

.notice .actions {
  align-self: stretch;
  justify-content: stretch;
}

.notice .actions > * {
  flex: 1;
}

/* ---------- Floating: menu, select, popover, tooltip ---------- */
.positioner {
  z-index: 50;
}

.floating {
  min-width: 12rem;
  padding: var(--space-1);
  background: var(--surface);
  color: var(--text);
  border: var(--border-width) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-raised);
  transition: opacity var(--duration-fast) var(--ease-standard);
}

.floating[data-starting-style],
.floating[data-ending-style] {
  opacity: 0;
}

.item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--tap-min);
  padding: 0 var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--text-md);
  cursor: default;
  user-select: none;
}

.item[data-highlighted] {
  background: var(--surface-2);
  outline: none;
}

.item[data-disabled] {
  opacity: var(--disabled-opacity);
}

.itemDestructive {
  color: var(--danger-text);
}

.itemIndicator {
  margin-left: auto;
  color: var(--primary);
}

.popoverPopup {
  composes: floating;
  padding: var(--space-3);
}

.tooltipPopup {
  padding: var(--space-1) var(--space-3);
  background: var(--text);
  color: var(--bg);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}

/* ---------- Select ---------- */
.fieldLabel {
  display: block;
  margin-bottom: var(--space-2);
  font-family: var(--font-heading);
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--text);
}

.selectTrigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-width: min(12rem, 100%);
  max-width: 100%;
  min-height: var(--control-height);
  padding: 0 var(--space-4);
  font-family: var(--font-body);
  font-size: var(--text-md);
  background: var(--surface);
  color: var(--text);
  border: var(--border-width-strong) solid var(--field-border);
  border-radius: var(--radius-md);
}

.selectTrigger[data-invalid] {
  border-color: var(--danger);
}

/* ---------- Tabs ---------- */
.tabList {
  position: relative;
  display: flex;
  gap: var(--space-1);
  padding: var(--space-1);
  background: var(--surface-2);
  border-radius: var(--radius-md);
  overflow-x: auto;
}

.tab {
  flex: 1 0 auto;
  min-height: var(--tap-min);
  padding: 0 var(--space-4);
  font-family: var(--font-heading);
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--text-2-strong);
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
}

.tab[data-active] {
  color: var(--text);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.tabPanel {
  padding-top: var(--space-5);
}

/* ---------- Switch ---------- */
.switchRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  min-height: var(--tap-min);
}

.switchText {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.switchLabel {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}

.switchDescription {
  font-size: var(--text-sm);
  color: var(--text-2);
}

.switchRoot {
  position: relative;
  flex-shrink: 0;
  width: 2.75rem;
  height: 1.625rem;
  padding: 0;
  background: var(--border);
  border: 0;
  border-radius: var(--radius-pill);
  transition: background var(--duration-fast) var(--ease-standard);
}

.switchRoot[data-checked] {
  background: var(--success);
}

.switchThumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1.25rem;
  height: 1.25rem;
  background: var(--surface);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-card);
  transition: translate var(--duration-fast) var(--ease-standard);
}

.switchThumb[data-checked] {
  translate: 1.125rem 0;
}

/* ---------- Text field ---------- */
.field {
  display: flex;
  flex-direction: column;
}

.control {
  width: 100%;
  min-height: var(--control-height);
  padding: var(--space-3) var(--space-4);
  font-family: var(--font-body);
  font-size: var(--text-md);
  background: var(--surface);
  color: var(--text);
  border: var(--border-width-strong) solid var(--field-border);
  border-radius: var(--radius-md);
}

.control::placeholder {
  color: var(--text-3);
}

.control[data-invalid] {
  border-color: var(--danger);
}

textarea.control {
  min-height: 7rem;
  resize: vertical;
}

.hint {
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-2);
}

.errorWrap {
  display: block;
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--danger-text);
}

.errorIcon {
  flex: none;
}

/* A control inside the right edge of a field (the password show/hide button). */
.controlWrap {
  position: relative;
  width: 100%;
}

.controlWithTrailing {
  padding-right: calc(var(--space-4) + var(--tap-min));
}

.trailing {
  position: absolute;
  top: 50%;
  right: var(--space-1);
  display: inline-flex;
  transform: translateY(-50%);
}

.eye {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--tap-min);
  height: var(--tap-min);
  padding: 0;
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: 0;
  border-radius: var(--radius-md);
}

.eye:hover {
  color: var(--text);
}

.control:disabled {
  color: var(--text-3);
  cursor: not-allowed;
  background: var(--surface-2);
}

.control[readonly] {
  background: var(--surface-2);
}

/* ---------- Phone field: country chooser (specs/006 US3) ---------- */
.phoneRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: stretch;
}

.countryTrigger {
  display: inline-flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  min-width: 5.5rem;
  min-height: var(--control-height);
  padding: 0 var(--space-3);
  font-family: var(--font-body);
  font-size: var(--text-md);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: var(--surface);
  border: var(--border-width-strong) solid var(--field-border);
  border-radius: var(--radius-md);
}

.phoneRow .control {
  flex: 1 1 10rem;
  min-width: 0;
}

.countryPopup {
  composes: floating;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(22rem, calc(100vw - 2 * var(--space-4)));
  min-width: 0;
  max-width: calc(100vw - 2 * var(--space-4));
  padding: var(--space-2);
}

.countrySearch {
  composes: control;
}

.countryList {
  max-height: 18rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.countryOption {
  composes: item;
}

.countryOption[data-selected] {
  background: var(--primary-soft);
}

.countryName {
  flex: 1;
  min-width: 0;
}

.countryDial {
  color: var(--text-2-strong);
}

.countryOption[data-selected] .countryDial {
  color: var(--text);
}

.countryEmpty {
  padding: var(--space-3);
  color: var(--text-2);
  text-align: center;
}

.countryEmpty:empty {
  display: none;
}

/* ---------- Toast ---------- */
.toastViewport {
  position: fixed;
  z-index: 60;
  right: var(--space-5);
  bottom: var(--space-5);
  left: var(--space-5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  width: min(26rem, 100%);
  padding: var(--space-4);
  background: var(--text);
  color: var(--bg);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-raised);
  pointer-events: auto;
}

.toastText {
  flex: 1;
}

.toastTitle {
  font-family: var(--font-heading);
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
}

.toastDescription {
  margin-top: var(--space-1);
  font-size: var(--text-sm);
}

.toastClose {
  composes: closeButton;
  color: inherit;
}
